/**
 * Catálogo do domínio `field` (T53, CAT-02).
 *
 * Origem: `src/shared/autorizacao/FieldServices.ts`.
 *
 * **Duas divergências herdadas, do mesmo formato das de `automacao`:**
 *
 * 1. `FieldServices.ts:8-16` fazia `return build()` no ramo de `admin_field`,
 *    descartando os ramos seguintes. Catálogo é aditivo: `field.admin` dá
 *    `manage` em `FIELD`, `DiarioOs` e `EapOS`, e o que o admin passa a ter a
 *    mais é `create` em `OS_FIELD`, que antes ele só teria se **não** fosse
 *    admin. A conferência de acessos (FLD-01, 2026-10-01) decidiu manter e
 *    escrever a concessão no próprio papel: `{create, read}` em `OS_FIELD`.
 *    Está declarada em `DIVERGENCIAS_DECIDIDAS` (`equivalencia.spec.ts`).
 * 2. ~~`FieldServices.ts:41` concede `create` e `read` em `OS_FIELD` a
 *    `site_manager`, e o `read` não tem papel que o carregue.~~ **Resolvido na
 *    T128 (AD-031 D3):** nasceu `field.criacao`, e `GRUPOS.SITE_MANAGER` o
 *    carrega. **O `read` entrou junto**, amplo e sem escopo (`acoes:
 *    ["create", "read"]` em `field.criacao`) — decisão do usuário em AD-034 D1,
 *    a mesma
 *    de `automacao.criacao`, cuja nota traz o motivo completo. Este cabeçalho
 *    dizia o contrário desde `01472581` e foi corrigido em 2026-09-06: o
 *    cabeçalho é o primeiro texto que alguém lê, e ele estava desmentindo o
 *    código.
 */
import type { NomePapel, Papel } from "../catalogo/vocabulario";

export const PAPEIS_FIELD = {
  "field.admin": {
    dominio: "field",
    nivel: "admin",
    concessoes: [
      { acoes: ["manage"], recurso: "FIELD" },
      { acoes: ["create", "read"], recurso: "OS_FIELD" },
      { acoes: ["manage"], recurso: "DiarioOs", condicao: { area: "FIELD" } },
      { acoes: ["manage"], recurso: "EapOS", condicao: { area: "FIELD" } },
    ],
  },
  "field.config": {
    dominio: "field",
    nivel: "config",
    concessoes: [{ acoes: ["config"], recurso: "FIELD", condicao: { area: "FIELD" } }],
  },
  /**
   * `DiarioOs` e `EapOS` entram com duas concessões cada, uma por área — é o que
   * `FieldServices.ts:28-39` faz, e é o que o compilador espera: `condicao` é
   * igualdade simples, não conjunto.
   */
  "field.escrita": {
    dominio: "field",
    nivel: "escrita",
    concessoes: [
      { acoes: ["read", "create"], recurso: "FIELD" },
      { acoes: ["create"], recurso: "OS_FIELD" },
      { acoes: ["create"], recurso: "DiarioOs", condicao: { area: "FIELD" } },
      { acoes: ["create"], recurso: "DiarioOs", condicao: { area: "POS" } },
      { acoes: ["create"], recurso: "EapOS", condicao: { area: "FIELD" } },
      { acoes: ["create"], recurso: "EapOS", condicao: { area: "POS" } },
    ],
  },
  /**
   * O apoio a OS que `field.escrita` carrega — diário e EAP de OS, nas áreas FIELD e
   * POS — sem o módulo `FIELD` (FLD-02, conferência de acessos, 2026-10-01).
   *
   * `GRUPOS.SITE_MANAGER` levava `field.escrita` inteira e, com ela, `read` e
   * `create` em `FIELD`: o site manager passava a abrir o módulo de Field Services.
   * Ele precisa só do diário e da EAP das OS (e de abrir OS, via `field.criacao`).
   * `field.escrita` não muda: a role `field` continua lendo e criando em `FIELD`.
   */
  "field.apoio_os": {
    dominio: "field",
    nivel: "apoio_os",
    concessoes: [
      { acoes: ["create"], recurso: "DiarioOs", condicao: { area: "FIELD" } },
      { acoes: ["create"], recurso: "DiarioOs", condicao: { area: "POS" } },
      { acoes: ["create"], recurso: "EapOS", condicao: { area: "FIELD" } },
      { acoes: ["create"], recurso: "EapOS", condicao: { area: "POS" } },
    ],
  },
  /**
   * Criar e ler OS de field (T128, AD-031 D3 mais AD-034). Par de
   * `automacao.criacao` — a nota completa, com a decisão da leitura ampla, está lá.
   *
   * `OS_FIELD` é gate de módulo puro, como `OS_AUTOMACAO`: é a string que
   * `ListarOsTicket.vue:19,29` usa para os botões "NOVA OS COMISSIONAMENTO" e
   * "NOVA OS FIELD".
   *
   * `field.escrita` já concede `create` em `OS_FIELD` (`FieldServices.ts:31`),
   * então quem tem a escrita não ganha nada com este papel. Ele existe para quem
   * **não** tem `field` e mesmo assim abre OS — que é o caso do site manager.
   */
  "field.criacao": {
    dominio: "field",
    nivel: "criacao",
    concessoes: [{ acoes: ["create", "read"], recurso: "OS_FIELD" }],
  },
  /** Sem chave em `LEGADO` e sem regra própria: existe porque `GRUPOS.MKT` o cita. */
  "field.leitura": {
    dominio: "field",
    nivel: "leitura",
    concessoes: [{ acoes: ["read"], recurso: "FIELD" }],
  },
} as const satisfies Partial<Record<NomePapel, Papel>>;
